import React from 'react';

/**
 * parseRequirements — parses a requirements fenced code block body.
 *
 * Format (each block separated by ##):
 *
 *   ## Optional title
 *   Description text (shown to students and in ERD panel)
 *
 *   Rubric:
 *   Rubric items (hidden from rendered output; used by AI feedback)
 *
 *   Questions:
 *   Questions (hidden from rendered output; used by AI feedback)
 *
 * Returns: Array<{ description: string, rubric: string, questions: string }>
 */
export function parseRequirements(content) {
  if (!content || !content.trim()) return [];

  // Split on ## boundaries; first segment may be empty if content starts with ##
  const blocks = content.split(/^##[ \t]*/m).filter(b => b.trim());

  return blocks.map(block => {
    const lines = block.split('\n');

    // First line could be an optional title heading
    let title = '';
    let body = block;
    const firstLine = lines[0]?.trim();
    if (firstLine && !firstLine.match(/^(Rubric:|Questions:)/i)) {
      title = firstLine;
      body = lines.slice(1).join('\n');
    }

    // Locate optional section markers
    const rubricIdx = body.search(/^Rubric:/im);
    const questionsIdx = body.search(/^Questions:/im);

    // Description ends at whichever marker comes first
    const descEnd = Math.min(
      rubricIdx >= 0 ? rubricIdx : Infinity,
      questionsIdx >= 0 ? questionsIdx : Infinity
    );
    const descBody = (descEnd === Infinity ? body : body.slice(0, descEnd)).trim();
    const description = [title, descBody].filter(Boolean).join('\n').trim();

    // Extract rubric text (between Rubric: and Questions: or end)
    let rubric = '';
    if (rubricIdx >= 0) {
      const after = body.slice(rubricIdx).replace(/^Rubric:/i, '');
      const qIdx = after.search(/^Questions:/im);
      rubric = (qIdx >= 0 ? after.slice(0, qIdx) : after).trim();
    }

    // Extract questions text (from Questions: to end)
    let questions = '';
    if (questionsIdx >= 0) {
      questions = body.slice(questionsIdx).replace(/^Questions:/i, '').trim();
    }

    return { description, rubric, questions };
  }).filter(r => r.description);
}

/**
 * RequirementsSnippet
 *
 * Renders a numbered list of requirements.
 * - Students: description only.
 * - Educators: description + nested rubric and questions subsections.
 *
 * Props:
 *   content   {string}  — raw text from the ```requirements:env fenced block
 *   env       {string}  — environment name extracted from the info string
 *   isStudent {boolean} — hides rubric/questions when true
 */
const RequirementsSnippet = ({ content, env, isStudent = false }) => {
  const requirements = parseRequirements(content);

  if (requirements.length === 0) {
    return (
      <div className="my-4 p-4 border border-dashed border-gray-300 rounded-lg text-sm text-gray-400 italic">
        No requirements defined{env ? ` for "${env}"` : ''}.
      </div>
    );
  }

  return (
    <div className="my-4 border border-blue-200 rounded-lg overflow-hidden">
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center space-x-2">
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
        <span className="text-sm font-semibold text-blue-800">Requirements</span>
        {env && (
          <span className="text-xs bg-blue-200 text-blue-700 px-2 py-0.5 rounded font-mono">{env}</span>
        )}
        <span className="ml-auto text-xs text-blue-500">{requirements.length} item{requirements.length !== 1 ? 's' : ''}</span>
      </div>
      <ol className="list-none p-4 space-y-4 bg-white">
        {requirements.map((req, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-800 leading-relaxed">{req.description}</p>

              {/* Educator-only: rubric and questions */}
              {!isStudent && (
                <div className="mt-2 space-y-2">
                  {req.rubric && (
                    <div>
                      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">Rubric</p>
                      <ul className="list-disc list-inside space-y-0.5 pl-1">
                        {req.rubric.split('\n').filter(Boolean).map((line, j) => (
                          <li key={j} className="text-xs text-amber-800 leading-relaxed">{line.replace(/^[-*]\s*/, '')}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {req.questions && (
                    <div>
                      <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-1">Questions</p>
                      <ul className="list-disc list-inside space-y-0.5 pl-1">
                        {req.questions.split('\n').filter(Boolean).map((line, j) => (
                          <li key={j} className="text-xs text-purple-800 leading-relaxed">{line.replace(/^[-*]\s*/, '')}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default RequirementsSnippet;
